import React, { RefAttributes, forwardRef, useMemo } from 'react';
import styled from 'styled-components';
import { Typography, TypographySize } from '@clds/typography';
import { Layout, Tab, Tabs } from '@clds/tabs';
import { Snippet } from '../types';
import { useTabId } from '../hooks';

export interface TabsHeaderProps {
  activeSnippet: Snippet;
  snippets: Snippet[];
  onSnippetChange: (lang: string) => void;
  ref: RefAttributes<HTMLDivElement>;
  isVisible?: boolean;
  onCopy?: () => void;
  tabsSize: TypographySize;
}

const ColoredTypography = styled(Typography)`
  // Inherit color from <Tab> component.
  color: inherit;
`;

const StyledTabs = styled(Tabs)`
  // Fix Windows "scroll-breaks-vertical-alignment" issue.
  // It's not scrollable, but changed to dropdown anyway.
  overflow: hidden;
`;

export const TabsHeader = styled(
  forwardRef<HTMLDivElement, TabsHeaderProps>(({ snippets, activeSnippet, onSnippetChange, isVisible, tabsSize, ...rest }, ref) => {
    const tabId = useTabId();
    const language = activeSnippet?.language;

    const activeTabId = useMemo(() => {
      const snippet = snippets.find((snippet) => snippet.language === language);
      return snippet ? snippets.indexOf(snippet) : 0;
    }, [language, snippets]);

    return (
      <StyledTabs
        style={isVisible ? { visibility: 'hidden' } : undefined}
        variant="thin"
        layout={Layout.INLINE}
        activeTabId={activeTabId}
        {...rest}
        ref={ref}
      >
        {snippets.map((snippet: Snippet, index) => {
          const onClick = () => onSnippetChange(snippet.language);
          return (
            <Tab
              key={snippet.language}
              id={index}
              onClick={onClick}
              aria-controls={`tab-${tabId}-${slugify(snippet.language)}`}
              data-test-specifier={`code-snippets-${slugify(snippet.language)}-language-tab`}
            >
              <ColoredTypography size={tabsSize} type="regular">
                {snippet.language}
              </ColoredTypography>
            </Tab>
          );
        })}
      </StyledTabs>
    );
  })
)`
  align-items: center;
`;

const slugify = (value: string) =>
  value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9 -]/g, '') // remove non-alphanumeric characters
    .replace(/\s+/g, '-');
